import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // Below this width (px) the line runs down the left (layoutNarrow).
  static NARROW = 640

  static targets = ["sentinel", "loader", "silhouette"]
  static values = {
    url: String,
    query: String,
    startMonth: String, // "YYYY-MM"
    endMonth: String,   // "YYYY-MM"
    totalFigures: Number,
    dateField: String,
    perPage: Number,
    direction: String, // "asc" or "desc"
    // Inline mode: when `items` is non-empty, the controller renders the
    // provided items synchronously instead of paginating against a JSON
    // endpoint. Used by callers that already have the full dataset on the
    // page (e.g. a custom figure's build photos), so we skip the fetch +
    // infinite-scroll plumbing entirely.
    items: Array,
    // When true, clicking a thumbnail opens the page-level lightbox via
    // `window.showLightboxImage` (using each item's `full_webp` / `full_avif`
    // / `caption`) instead of navigating to `figure_path`. Pairs naturally
    // with inline mode for image-only timelines.
    lightbox: Boolean,
    // When true (a brand's Timeline tab), each figure is a small figure card
    // (picture, status ribbon, name) and a month's figures read in date
    // order, left to right across the line, row by row.
    cards: Boolean
  }

  connect() {
    this.itemsEl = this.element.querySelector('.timeline-items')
    this.page = 1
    this.loaded = 0
    this.loading = false
    this.monthCounts = {} // month_key => count of figures rendered
    this.monthIndexByKey = {} // month_key => index from start (0-based)
    this.monthBaseSide = {} // month_key => 'left' | 'right' (used only for singletons)
    this.monthFigures = {} // month_key => array of figure elements (in arrival order)
    // Months that have at least one item, computed up-front in inline
    // mode so `buildSkeleton` can collapse runs of empty months into a
    // single "torn timeline" break instead of rendering vertical
    // dead space for every gap month.
    this.populatedMonthKeys = null

    this.activeMonthKey = null
    this.monthObserver = null

    // Pre-compute populated months in inline mode -- the buildSkeleton
    // pass uses this to skip empty months and emit gap breaks.
    if (this.hasItemsValue && this.itemsValue.length > 0) {
      this.populatedMonthKeys = new Set(this.itemsValue.map(i => i.month_key).filter(Boolean))
    }

    // Build skeleton months (from YYYY-MM values)
    this.buildSkeleton()

    // Ensure loader/sentinel live under items container
    this.prepareLoaderAndSentinel()

    // Center fade visuals -- needed in both inline and AJAX modes so the
    // viewport spotlight effect still works.
    this.boundUpdateVisuals = this.rafThrottle(() => this.updateCenterFades())
    window.addEventListener('scroll', this.boundUpdateVisuals, { passive: true })
    window.addEventListener('resize', this.boundUpdateVisuals)

    // Re-run the wrap layout whenever the container's box changes size.
    // Fires on window resize / orientation change AND when the timeline
    // transitions from hidden (0x0, e.g. before the build-photos toggle
    // reveals it) to its real width, so the adaptive grid picks the
    // right columns-per-side count.
    if (typeof ResizeObserver !== 'undefined') {
      const debouncedRelayout = this.debounce(() => this.relayoutAllMonths(), 100)
      this.resizeObserver = new ResizeObserver(debouncedRelayout)
      this.resizeObserver.observe(this.element)
    }

    if (this.hasItemsValue && this.itemsValue.length > 0) {
      // Inline mode: the page already supplied the full item list, so we
      // render everything in one pass and skip the loader / sentinel /
      // observer entirely. We also precompute a lightbox media set when
      // `lightboxValue` is true so each thumbnail's click handler can
      // dispatch to the page-level lightbox without re-walking the DOM.
      this.renderInlineItems()
      return
    }

    // A #YYYY or #YYYY-MM in the URL (a brand's Production chart links
    // here so) opens the timeline at that month.
    this.jumpToHash()
    // ...and so does a new one (the panorama above the line sets it).
    this.boundHashChange = () => this.onHashChange()
    window.addEventListener('hashchange', this.boundHashChange)

    // Intersection observer for infinite scrolling (AJAX mode)
    this.setupObserver()
    // Fallback scroll/resize detection
    this.boundCheckAndLoad = this.debounce(() => this.checkAndLoad(), 150)
    window.addEventListener('scroll', this.boundCheckAndLoad, { passive: true })
    window.addEventListener('resize', this.boundCheckAndLoad)

    // Ensure first page loads immediately
    // Use rAF to ensure DOM is settled
    requestAnimationFrame(() => this.loadNextPage())
  }

  renderInlineItems() {
    const items = this.itemsValue
    this.totalFiguresValue = items.length

    // Notes (a custom's build-journal entries) sit on the timeline but
    // aren't pictures, so they're left out of the lightbox's set.
    const pictures = items.filter((item) => item.kind !== 'note')
    if (this.lightboxValue) {
      // Build once; insertFigure -> buildFigureElement reaches back into
      // this on click to find the right index and reuse the media set.
      this.lightboxMediaSet = pictures.map((item) => ({
        type: 'image',
        url: item.full_webp || item.image_url || '',
        avifUrl: item.full_avif || null,
        caption: item.caption || ''
      }))
    }

    items.forEach((item) => {
      this.insertFigure({ ...item, _idx: pictures.indexOf(item) })
    })
    this.loaded = items.length

    // No more pages to load -- hide and detach the loader/sentinel so they
    // don't take up space at the bottom of the timeline.
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add('hidden')
      this.loaderTarget.remove()
    }
    if (this.hasSentinelTarget) {
      this.sentinelTarget.remove()
    }

    // Run one fade pass now that everything is in the DOM.
    this.updateCenterFades()
  }

  disconnect() {
    try {
      if (this.observer && this.hasSentinelTarget) {
        this.observer.unobserve(this.sentinelTarget)
      }
      if (this.boundUpdateVisuals) {
        window.removeEventListener('scroll', this.boundUpdateVisuals)
        window.removeEventListener('resize', this.boundUpdateVisuals)
      }
      if (this.boundHashChange) window.removeEventListener('hashchange', this.boundHashChange)
      if (this.boundCheckAndLoad) {
        window.removeEventListener('scroll', this.boundCheckAndLoad)
        window.removeEventListener('resize', this.boundCheckAndLoad)
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    } catch (e) {}
  }

  prepareLoaderAndSentinel() {
    if (this.hasLoaderTarget) {
      // Move loader under items container and place before sentinel for better UX
      if (this.loaderTarget.parentElement !== this.itemsEl) {
        this.itemsEl.appendChild(this.loaderTarget)
      }
    }
    if (this.hasSentinelTarget) {
      if (this.sentinelTarget.parentElement !== this.itemsEl) {
        this.itemsEl.appendChild(this.sentinelTarget)
      }
      if (this.hasLoaderTarget) {
        this.itemsEl.insertBefore(this.sentinelTarget, this.loaderTarget.nextSibling)
      }
    }
  }

  buildSkeleton() {
    this.itemsEl.innerHTML = ""
    this.monthCounts = {}
    this.monthIndexByKey = {}
    this.monthBaseSide = {}
    this.monthFigures = {}

    if (!this.startMonthValue || !this.endMonthValue) {
      return
    }
    const [startYear, startMonth] = this.parseYm(this.startMonthValue)
    const [endYear, endMonth] = this.parseYm(this.endMonthValue)

    const descending = (this.directionValue || 'desc') === 'desc'
    // Collect every month between start and end into a flat ordered
    // list so we can decide -- before rendering anything -- which ones
    // are populated, where the gap runs are, and emit a single
    // "torn timeline" break per run instead of N empty month groups.
    const months = []
    if (descending) {
      let y = endYear, m = endMonth
      while (y > startYear || (y === startYear && m >= startMonth)) {
        months.push([y, m])
        ;[y, m] = this.prevYm(y, m)
      }
    } else {
      let y = startYear, m = startMonth
      while (y < endYear || (y === endYear && m <= endMonth)) {
        months.push([y, m])
        ;[y, m] = this.nextYm(y, m)
      }
    }

    // Inline mode is the only one where we know which months are
    // populated up-front; AJAX mode loads pages on demand so we can't
    // decide what's empty yet and have to keep rendering every month.
    const knowsPopulated = this.populatedMonthKeys !== null
    // Min run length that counts as "worth showing a break for". Two
    // consecutive empties is enough to justify the visual save -- a
    // single empty month doesn't take up much vertical space and
    // collapsing it would be inconsistent with the surrounding rhythm.
    const MIN_GAP_FOR_BREAK = 2

    let idx = 0 // index across rendered (populated) months only
    let i = 0
    while (i < months.length) {
      const [y, m] = months[i]
      const monthKey = this.ymKey(y, m)
      const isPopulated = !knowsPopulated || this.populatedMonthKeys.has(monthKey)

      if (!isPopulated && knowsPopulated) {
        // Look ahead to the end of this empty run.
        let j = i
        while (j < months.length) {
          const [yy, mm] = months[j]
          if (this.populatedMonthKeys.has(this.ymKey(yy, mm))) break
          j++
        }
        const runLen = j - i
        // We only emit a break when the run sits BETWEEN two populated
        // months -- no point putting a "torn timeline" at the very
        // top/bottom where there's nothing to tear away from.
        const hasNeighborBefore = idx > 0
        const hasNeighborAfter = j < months.length
        if (runLen >= MIN_GAP_FOR_BREAK && hasNeighborBefore && hasNeighborAfter) {
          this.itemsEl.appendChild(this.buildGapBreak(months[i], months[j - 1], runLen))
          i = j
          continue
        }
        // Run too short (or at the edge): render the empty months
        // normally so the surrounding rhythm doesn't jump.
      }

      this.monthIndexByKey[monthKey] = idx
      this.monthCounts[monthKey] = 0
      this.monthBaseSide[monthKey] = (idx % 2 === 0) ? 'left' : 'right'
      this.monthFigures[monthKey] = []
      this.itemsEl.appendChild(this.buildMonthGroup(y, m, monthKey))
      idx++
      i++
    }
  }

  // Builds a "torn timeline" break element used in place of a run of
  // consecutive empty months on the build-photos timeline. The label
  // describes the skipped span ("3 months skipped" / "Jan – Mar 2024
  // skipped") so the visual discontinuity stays informative.
  buildGapBreak(firstSkipped, lastSkipped, runLen) {
    const wrapper = document.createElement('div')
    wrapper.className = 'timeline-gap-break'
    wrapper.dataset.role = 'gap-break'

    const spine = document.createElement('div')
    spine.className = 'gap-spine'
    wrapper.appendChild(spine)

    const zig = document.createElement('div')
    zig.className = 'gap-zigzag'
    wrapper.appendChild(zig)

    const label = document.createElement('div')
    label.className = 'gap-label'
    label.textContent = this.gapLabelText(firstSkipped, lastSkipped, runLen)
    wrapper.appendChild(label)

    return wrapper
  }

  gapLabelText(firstSkipped, lastSkipped, runLen) {
    const SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    // Always render in chronological order regardless of which way
    // the surrounding timeline is iterating (asc vs desc), so the
    // label reads naturally ("Jan – Mar 2024 skipped").
    const [yA, mA] = firstSkipped
    const [yB, mB] = lastSkipped
    const aIsEarlier = (yA < yB) || (yA === yB && mA < mB)
    const [y1, m1] = aIsEarlier ? [yA, mA] : [yB, mB]
    const [y2, m2] = aIsEarlier ? [yB, mB] : [yA, mA]
    if (y1 === y2 && m1 === m2) return `${SHORT[m1 - 1]} ${y1} skipped`
    const sameYear = y1 === y2
    const left  = sameYear ? `${SHORT[m1 - 1]}` : `${SHORT[m1 - 1]} ${y1}`
    const right = `${SHORT[m2 - 1]} ${y2}`
    return `${left} – ${right} skipped (${runLen} months)`
  }

  setupObserver() {
    try {
      this.observer?.disconnect()
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            
            this.loadNextPage()
          }
        }
      }, { root: null, rootMargin: '1000px 0px', threshold: 0 })
      if (this.hasSentinelTarget) {
        this.observer.observe(this.sentinelTarget)
      }
    } catch (e) {
      
    }
  }

  checkAndLoad() {
    if (!this.hasSentinelTarget) return
    const rect = this.sentinelTarget.getBoundingClientRect()
    const viewportH = window.innerHeight || document.documentElement.clientHeight
    // If the sentinel is within 1500px of the viewport bottom, load next page
    if (rect.top < viewportH + 1500) {
      this.loadNextPage()
    }
  }

  debounce(fn, wait) {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  buildMonthGroup(year, month, monthKey) {
    const wrapper = document.createElement('div')
    wrapper.className = 'timeline-date-group relative'
    wrapper.dataset.date = monthKey

    const label = document.createElement('div')
    const isDesc = (this.directionValue || 'desc') === 'desc'
    const pos = isDesc ? 'bottom-0 translate-y-1/2' : 'top-0 -translate-y-1/2'
    label.className = `issue-label absolute ${pos} left-1/2 transform -translate-x-1/2 z-20`
    label.innerHTML = `<span class="issue-label-text font-comic">${this.formatMonthYear(year, month)}</span>`

    const figures = document.createElement('div')
    figures.className = 'timeline-figures relative mt-12 min-h-[140px]'
    figures.style.height = '140px'

    wrapper.appendChild(label)
    wrapper.appendChild(figures)
    return wrapper
  }

  formatMonthYear(year, month) {
    // Avoid timezone shifts by not using Date; render from integers
    const MONTHS = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December'
    ]
    const idx = Math.max(1, Math.min(12, month)) - 1
    return `${MONTHS[idx]} ${year}`
  }

  // rAF throttle helper
  rafThrottle(fn) {
    let queued = false
    return (...args) => {
      if (queued) return
      queued = true
      requestAnimationFrame(() => {
        queued = false
        fn.apply(this, args)
      })
    }
  }

  updateCenterFades() {
    const figures = this.itemsEl.querySelectorAll('.timeline-figure .image-container')
    if (!figures || figures.length === 0) return
    // If the entire timeline is hidden (e.g. the build-photos toggle is
    // currently showing the grid view), `getBoundingClientRect()` would
    // return zeros for every figure and we'd dim them all to 20% opacity
    // -- which would then persist when the timeline is revealed. Bail
    // early so the visuals only run when we're actually on screen.
    if (this.element.offsetParent === null) return
    const viewportH = window.innerHeight || document.documentElement.clientHeight
    const bandEdge = 100 // fade zone at top/bottom of viewport
    for (const container of figures) {
      const rect = container.getBoundingClientRect()
      const figCenter = rect.top + rect.height / 2
      const distToTop = Math.max(0, figCenter - 0)
      const distToBottom = Math.max(0, viewportH - figCenter)
      const edgeProximity = Math.min(distToTop, distToBottom)
      container.style.transition = 'filter 140ms linear, opacity 140ms linear, transform 140ms ease'
      if (edgeProximity < bandEdge) {
        // Inside top/bottom fade bands: stronger fade over 100px
        const t = Math.max(0, Math.min(1, edgeProximity / bandEdge)) // 0 at edge, 1 at band boundary
        const brightness = 0.2 + 0.8 * t
        const opacity = 0.2 + 0.8 * t
        container.classList.remove('center-highlight')
        const fig = container.closest('.timeline-figure')
        if (fig) {
          fig.dataset.highlighted = '0'
        }
        container.style.filter = `brightness(${brightness})`
        container.style.opacity = `${opacity}`
      } else {
        // Fully lit/highlighted across the rest of the viewport
        container.classList.add('center-highlight')
        container.style.filter = 'brightness(1)'
        container.style.opacity = '1'
        // Trigger spark once on entry into highlight
        const fig = container.closest('.timeline-figure')
        if (fig && fig.dataset.highlighted !== '1') {
          this.queueConnectorSpark(fig)
          fig.dataset.highlighted = '1'
        }
      }
    }
  }

  async loadNextPage() {
    if (this.loading) return
    if (this.totalFiguresValue && this.loaded >= this.totalFiguresValue) return
    this.loading = true
    this.showLoader(true)
    try {
      const perPage = this.perPageValue || 50
      const base = this.urlValue
      const params = new URLSearchParams(this.queryValue || "")
      params.set("page", String(this.page))
      params.set("per_page", String(perPage))
      const url = `${base}?${params.toString()}`
      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } })
      if (!resp.ok) throw new Error(`Failed to fetch timeline data: ${resp.status}`)
      const data = await resp.json()
      if (!this.totalFiguresValue && typeof data.total === 'number') {
        this.totalFiguresValue = data.total
        // If there are no results, render empty state once and stop
        if (this.totalFiguresValue <= 0) {
          this.renderEmpty()
          this.showLoader(false)
          return
        }
      }
      const items = Array.isArray(data.items) ? data.items : []
      for (const item of items) {
        this.insertFigure(item)
      }
      this.loaded += items.length
      this.page += 1
      if (items.length) this.lastMonthKey = items[items.length - 1].month_key
      // Move sentinel to the end of currently loaded content so we trigger next page at the right place
      this.updateSentinelPosition()
      if (this.loaded >= (data.total ?? this.totalFiguresValue ?? 0)) {
        if (this.observer && this.hasSentinelTarget) {
          this.observer.unobserve(this.sentinelTarget)
        }
      }
    } catch (e) {
      console.error('[Timeline] load error', e)
    } finally {
      this.loading = false
      this.showLoader(false)
    }
    // Opening at a month: hold it in place while the months above fill in,
    // and keep loading until the figures reach it.
    if (this.anchorGroup) {
      this.holdAnchor()
      if (this.anchorReached()) this.releaseAnchor()
      else if (!this.totalFiguresValue || this.loaded < this.totalFiguresValue) requestAnimationFrame(() => this.loadNextPage())
    }
  }

  // The month group a #YYYY-MM (or the newest month of a #YYYY) names,
  // scrolled to until the figures have loaded down to it or you scroll.
  jumpToHash() {
    const m = (window.location.hash || "").match(/^#(\d{4})(?:-(\d{2}))?$/)
    if (!m) return
    const groups = Array.from(this.itemsEl.querySelectorAll('.timeline-date-group'))
    const group = m[2] ? groups.find(g => g.dataset.date === `${m[1]}-${m[2]}`) : groups.find(g => g.dataset.date.startsWith(`${m[1]}-`))
    if (!group) return
    this.anchorGroup = group
    this.anchorKey = group.dataset.date
    this.boundReleaseAnchor = () => this.releaseAnchor()
    for (const type of ['wheel', 'touchstart', 'keydown', 'mousedown']) {
      window.addEventListener(type, this.boundReleaseAnchor, { passive: true, once: true })
    }
    requestAnimationFrame(() => this.holdAnchor())
  }

  // A new #YYYY-MM while you're here: go to that month, loading down to it if it isn't in yet.
  onHashChange() {
    this.releaseAnchor()
    this.jumpToHash()
    if (!this.anchorGroup) return
    if (this.anchorReached()) requestAnimationFrame(() => { this.holdAnchor(); this.releaseAnchor() })
    else if (!this.loading) this.loadNextPage()
  }

  holdAnchor() {
    if (!this.anchorGroup) return
    const label = this.anchorGroup.querySelector('.issue-label') || this.anchorGroup
    // Below the sticky bar, with the month's figures (above its label, newest first) in view.
    const offset = (this.directionValue || 'desc') === 'desc' ? window.innerHeight * 0.6 : 180
    window.scrollTo({ top: window.scrollY + label.getBoundingClientRect().top - offset })
  }

  anchorReached() {
    if (!this.lastMonthKey) return false
    const desc = (this.directionValue || 'desc') === 'desc'
    return desc ? this.lastMonthKey < this.anchorKey : this.lastMonthKey > this.anchorKey
  }

  releaseAnchor() {
    if (!this.anchorGroup) return
    this.anchorGroup = null
    for (const type of ['wheel', 'touchstart', 'keydown', 'mousedown']) {
      window.removeEventListener(type, this.boundReleaseAnchor)
    }
  }

  insertFigure(item) {
    
    const monthKey = item.month_key
    const monthGroup = this.itemsEl.querySelector(`.timeline-date-group[data-date="${monthKey}"]`)
    if (!monthGroup) return
    const figuresEl = monthGroup.querySelector('.timeline-figures')
    const fig = this.buildFigureElement(item)
    figuresEl.appendChild(fig)

    // Track and layout month figures according to original logic
    const list = this.monthFigures[monthKey] || []
    list.push(fig)
    this.monthFigures[monthKey] = list
    this.layoutMonth(monthKey)

    // Update fades for the newly added element
    this.updateCenterFades()
  }

  renderEmpty() {
    
    this.itemsEl.innerHTML = `
      <div class="text-center text-gray-400 py-12">
        No figures match your filters.
      </div>
    `
  }

  // Helpers for YYYY-MM arithmetic
  parseYm(ym) {
    const [yStr, mStr] = ym.split('-')
    return [parseInt(yStr, 10), parseInt(mStr, 10)]
  }
  ymKey(y, m) {
    const mm = m < 10 ? `0${m}` : `${m}`
    return `${y}-${mm}`
  }
  nextYm(y, m) {
    if (m === 12) return [y + 1, 1]
    return [y, m + 1]
  }
  prevYm(y, m) {
    if (m === 1) return [y - 1, 12]
    return [y, m - 1]
  }

  // Build a figure DOM element (without positional styling)
  buildFigureElement(item) {
    if (item.kind === 'note') return this.buildNoteElement(item)
    if (this.cardsValue) return this.buildCardElement(item)
    const fig = document.createElement('div')
    fig.className = 'timeline-figure absolute top-1/2 -translate-y-1/2 z-20'
    fig.dataset.figureId = item.id

    // In lightbox mode the "link" is just a non-navigating container that
    // delegates clicks to the page-level lightbox; otherwise it's a real
    // anchor pointing at the figure's detail page (existing behavior).
    let link
    if (this.lightboxValue) {
      link = document.createElement('div')
      link.className = 'block group cursor-pointer'
      const idx = typeof item._idx === 'number' ? item._idx : 0
      link.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!window.showLightboxImage || !this.lightboxMediaSet) return
        const media = this.lightboxMediaSet[idx]
        if (!media) return
        window.showLightboxImage('', idx, this.lightboxMediaSet, media.caption || '')
      })
    } else {
      link = document.createElement('a')
      link.href = item.figure_path
      link.className = 'block group'
    }

    const imageContainer = document.createElement('div')
    // Centred in the 140px figure, so the picture sits the same distance
    // from its connector circle on either side of the spine.
    imageContainer.className = 'image-container loading figure-image-container relative z-10 w-32 h-32 mx-auto bg-gray-800 rounded-lg overflow-hidden group-hover:ring-4 group-hover:ring-yellow-400 transition thumbnail-128'

    if (item.video_url) {
      // A build-journal video plays in its tile, muted, on a loop.
      const video = document.createElement('video')
      Object.assign(video, { src: item.video_url, muted: true, loop: true, autoplay: true, playsInline: true })
      if (item.image_url) video.poster = item.image_url
      video.className = 'w-full h-full object-cover'
      video.addEventListener('loadeddata', () => {
        imageContainer.classList.remove('loading')
        imageContainer.classList.add('loaded')
      })
      imageContainer.appendChild(video)
    } else if (item.image_url) {
      const img = document.createElement('img')
      img.src = item.image_url
      img.loading = 'lazy'
      img.className = 'w-full h-full object-cover'
      img.style.opacity = '0'
      img.style.transition = 'opacity 0.3s ease-in-out'
      img.addEventListener('load', () => {
        imageContainer.classList.remove('loading')
        imageContainer.classList.add('loaded')
        img.style.opacity = '1'
      })
      imageContainer.appendChild(img)
    } else {
      const placeholder = document.createElement('div')
      placeholder.className = 'w-full h-full flex items-center justify-center text-gray-500'
      placeholder.innerHTML = '<i class="fas fa-image text-3xl"></i>'
      imageContainer.appendChild(placeholder)
    }

    const info = document.createElement('div')
    info.className = 'figure-info mt-2 text-center'
    const h3 = document.createElement('h3')
    // line-clamp-3 caps long titles/captions at three lines so they can't
    // overflow the row stride and visually collide with the row below
    // (build-photo captions are user-generated and can run long).
    h3.className = 'text-sm font-medium group-hover:text-yellow-400 transition line-clamp-3'
    h3.textContent = item.name
    const pVar = document.createElement('p')
    pVar.className = 'text-xs text-gray-400'
    pVar.textContent = item.variation || ''
    const pProp = document.createElement('p')
    pProp.className = 'text-xs text-gray-500'
    pProp.textContent = item.property || ''
    const pDate = document.createElement('p')
    pDate.className = 'text-xs text-yellow-300 mt-1'
    pDate.textContent = item.date_label

    info.appendChild(h3)
    info.appendChild(pVar)
    info.appendChild(pProp)
    info.appendChild(pDate)

    // Connector inside the figure so line is under image, but circle above
    const connector = document.createElement('div')
    connector.className = 'connector-line absolute top-1/2 h-0.5 bg-yellow-400 z-0 flex items-center pointer-events-none'
    const circle = document.createElement('div')
    circle.className = 'timeline-connector-circle w-3 h-3 bg-yellow-400 rounded-full absolute top-1/2 z-30'
    connector.appendChild(circle)

    link.appendChild(imageContainer)
    link.appendChild(info)
    fig.appendChild(link)
    fig.appendChild(connector)
    return fig
  }

  // A figure as a small figure card: its picture (else the silhouette) with
  // its status ribbon on the bottom edge, then its name, variation and date.
  // The card is the `.image-container` the centre highlight lights up.
  buildCardElement(item) {
    const fig = document.createElement('div')
    fig.className = 'timeline-figure absolute top-1/2 -translate-y-1/2 z-20'
    fig.dataset.figureId = item.id

    const card = document.createElement('a')
    card.href = item.figure_path
    card.className = `image-container tlc st-${item.status_kind || 'pre'}`

    const ph = document.createElement('span')
    ph.className = 'tlc-ph'
    if (item.image_url) {
      const img = document.createElement('img')
      img.src = item.image_url
      img.loading = 'lazy'
      img.alt = ''
      ph.appendChild(img)
    } else if (this.hasSilhouetteTarget) {
      ph.appendChild(this.silhouetteTarget.content.cloneNode(true))
    }
    if (item.status_label) {
      const ribbon = document.createElement('span')
      ribbon.className = 'tlc-ribbon'
      ribbon.innerHTML = `<i class="fas ${item.status_icon || 'fa-tag'}" aria-hidden="true"></i>`
      ribbon.append(item.status_label)
      ph.appendChild(ribbon)
    }

    const body = document.createElement('span')
    body.className = 'tlc-body'
    const nm = document.createElement('b')
    nm.className = 'tlc-nm'
    nm.textContent = item.name
    body.appendChild(nm)
    if (item.variation) {
      const v = document.createElement('span')
      v.className = 'tlc-var'
      v.textContent = item.variation
      body.appendChild(v)
    }
    const d = document.createElement('span')
    d.className = 'tlc-date'
    d.textContent = item.date_label
    body.appendChild(d)
    card.title = [item.name, item.variation, item.property].filter(Boolean).join(' · ')

    card.appendChild(ph)
    card.appendChild(body)

    const connector = document.createElement('div')
    connector.className = 'connector-line absolute top-1/2 h-0.5 bg-yellow-400 z-0 flex items-center pointer-events-none'
    const circle = document.createElement('div')
    circle.className = 'timeline-connector-circle w-3 h-3 bg-yellow-400 rounded-full absolute top-1/2 z-30'
    connector.appendChild(circle)

    fig.appendChild(card)
    fig.appendChild(connector)
    return fig
  }

  // A build-journal note: a comic caption box the size of a thumbnail (so
  // the month's layout treats it like any figure), with its date under it.
  buildNoteElement(item) {
    const fig = document.createElement('div')
    fig.className = 'timeline-figure timeline-note absolute top-1/2 -translate-y-1/2 z-20'
    const box = document.createElement('div')
    box.className = 'relative z-10 w-32 h-32 mx-auto overflow-hidden rounded-md border-2 border-black bg-yellow-300 text-gray-900 p-2 text-sm font-semibold leading-snug shadow-[3px_3px_0_#000]'
    const text = document.createElement('p')
    text.className = 'line-clamp-6'
    text.textContent = item.note
    box.title = item.note
    box.appendChild(text)
    const info = document.createElement('div')
    info.className = 'figure-info mt-2 text-center'
    const pDate = document.createElement('p')
    pDate.className = 'text-xs text-yellow-300'
    pDate.textContent = item.date_label
    info.appendChild(pDate)
    const connector = document.createElement('div')
    connector.className = 'connector-line absolute top-1/2 h-0.5 bg-yellow-400 z-0 flex items-center pointer-events-none'
    const circle = document.createElement('div')
    circle.className = 'timeline-connector-circle w-3 h-3 bg-yellow-400 rounded-full absolute top-1/2 z-30'
    connector.appendChild(circle)
    fig.appendChild(box)
    fig.appendChild(info)
    fig.appendChild(connector)
    return fig
  }

  // Layout a month's figures.
  //
  // Sparse months (1-2 figures) keep the original "single column on each
  // side, depth grows outward" layout so nothing changes visually for the
  // common case on the release timeline.
  //
  // Crowded months use an adaptive grid: figures pack out from the spine
  // at increasing depth until we'd push past the available horizontal
  // space, then wrap to a new row below. On wide screens you might get
  // 2-3 figures per side per row; on narrow / mobile widths you'll get
  // just one per side and lots of rows. Either way the layout never
  // exceeds the container width, so the page never scrolls sideways.
  layoutMonth(monthKey) {
    const list = this.monthFigures[monthKey] || []
    const count = list.length
    if (count === 0) return

    const monthGroup = this.itemsEl.querySelector(`.timeline-date-group[data-date="${monthKey}"]`)
    const figuresEl = monthGroup ? monthGroup.querySelector('.timeline-figures') : null

    // A phone (or any narrow column): the line runs down the left and the
    // figures sit to its right, as many to a row as fit.
    const width = (figuresEl && figuresEl.offsetWidth) || this.element.offsetWidth || 0
    this.element.classList.toggle('is-narrow', width > 0 && width < this.constructor.NARROW)
    if (width > 0 && width < this.constructor.NARROW) return this.layoutNarrow(list, figuresEl, width)

    // Cards read in date order across the line: each row's figures fill it
    // left to right, outermost left to outermost right.
    if (this.cardsValue) return this.layoutAcross(list, figuresEl, width)

    // --- Sparse month: 1 or 2 figures ---------------------------------
    if (count <= 2) {
      // Reset to the default single-row height in case this month was
      // previously laid out as a stacked grid (e.g. after items refresh).
      if (figuresEl) figuresEl.style.height = '140px'

      if (count === 1) {
        const side = this.monthBaseSide[monthKey] || 'left'
        this.applyFigurePosition(list[0], side, 1, 100)
        this.setConnectorVisible(list[0], true)
        return
      }
      for (let i = 0; i < count; i++) {
        const side = (i % 2 === 0) ? 'left' : 'right'
        this.applyFigurePosition(list[i], side, 1, 100)
        this.setConnectorVisible(list[i], true)
      }
      return
    }

    // --- Crowded month: 3+ figures, adaptive grid wrap ----------------
    const FIG_WIDTH      = 140 // matches .timeline-figure width
    const SPINE_INSET    = 100 // distance from spine to innermost figure edge
    const DEPTH_STRIDE   = 170 // additional horizontal distance per extra depth
    const SAFETY_MARGIN  = 16  // breathing room so figures don't kiss the page edge
    // Row stride sized for the worst-case build-photo card: 128px image +
    // up to 3 wrapped lines of caption + property/date lines + a bit of
    // air between rows. Captions get a CSS line-clamp at render time so
    // they can't exceed this, keeping rows from colliding visually.
    const FIG_VISUAL_H   = 240
    const ROW_GAP        = 32

    // Detect how many depth columns fit on each side of the spine inside
    // the current container width. Fall back to 1 column if the container
    // hasn't been measured yet (e.g. we're laying out while still hidden);
    // the ResizeObserver will re-run this once the element is visible.
    const containerWidth = (figuresEl && figuresEl.offsetWidth) || 0
    const halfWidth = containerWidth / 2
    let colsPerSide = 1
    while (true) {
      const nextDepth = colsPerSide + 1
      const nextOuterEdge = SPINE_INSET + (nextDepth - 1) * DEPTH_STRIDE + FIG_WIDTH
      if (nextOuterEdge > halfWidth - SAFETY_MARGIN) break
      colsPerSide = nextDepth
    }
    const figsPerRow = colsPerSide * 2

    const rows = Math.ceil(count / figsPerRow)
    const totalHeight = rows * FIG_VISUAL_H + Math.max(0, rows - 1) * ROW_GAP
    if (figuresEl) figuresEl.style.height = `${totalHeight}px`

    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / figsPerRow)
      const inRow = i % figsPerRow
      const side = (inRow % 2 === 0) ? 'left' : 'right'
      // Within a row: indices 0,2,4,... ride the left side at depths 1,2,3
      // (inner -> outer); indices 1,3,5,... do the same on the right.
      const depth = Math.floor(inRow / 2) + 1
      const offset = SPINE_INSET + (depth - 1) * DEPTH_STRIDE
      // Vertical center of this row, measured from the figures container's
      // top edge. The figure element keeps its `-translate-y-1/2` class so
      // it sits centered on this y coordinate (just like the single-row
      // case sits centered on `top: 50%`).
      const rowCenter = row * (FIG_VISUAL_H + ROW_GAP) + FIG_VISUAL_H / 2
      this.applyFigurePosition(list[i], side, depth, offset, rowCenter)
      // Only the innermost figure on each side gets a visible connector;
      // deeper figures would otherwise stack circles on the same spine
      // point per row, which reads as visual noise.
      this.setConnectorVisible(list[i], depth === 1)
    }
  }

  // How many figure columns fit on each side of the spine at this width.
  colsPerSide(width) {
    const FIG_WIDTH = 140, SPINE_INSET = 100, DEPTH_STRIDE = 170, SAFETY_MARGIN = 16
    let cols = 1
    while (SPINE_INSET + cols * DEPTH_STRIDE + FIG_WIDTH <= width / 2 - SAFETY_MARGIN) cols++
    return cols
  }

  // Cards mode: rows of up to 2 x colsPerSide figures, in the order they
  // came (the timeline's date order), filling each row left to right: the
  // first half on the left (outermost first), the rest on the right.
  layoutAcross(list, figuresEl, width) {
    const ROW_H = 236, ROW_GAP = 28
    const perRow = this.colsPerSide(width) * 2
    const rows = Math.ceil(list.length / perRow)
    if (figuresEl) {
      figuresEl.style.height = `${rows * ROW_H + (rows - 1) * ROW_GAP}px`
      // Room for the month's label where it straddles the row's edge.
      figuresEl.style.marginBottom = (this.directionValue || 'desc') === 'desc' ? '30px' : ''
    }
    for (let row = 0; row < rows; row++) {
      const inRow = list.slice(row * perRow, (row + 1) * perRow)
      const left = Math.ceil(inRow.length / 2)
      const rowCenter = row * (ROW_H + ROW_GAP) + ROW_H / 2
      inRow.forEach((fig, i) => {
        const side = i < left ? 'left' : 'right'
        const depth = side === 'left' ? left - i : i - left + 1
        this.applyFigurePosition(fig, side, depth, 100 + (depth - 1) * 170, rowCenter)
        this.setConnectorVisible(fig, depth === 1)
      })
    }
  }

  // Narrow: the line down the left edge, figures in rows to its right.
  layoutNarrow(list, figuresEl, width) {
    const FIG = 140, GAP = 12, SPINE = 18, INSET = 22, ROW_H = this.cardsValue ? 250 : 220, ROW_GAP = 24
    const perRow = Math.max(1, Math.floor((width - SPINE - INSET + GAP) / (FIG + GAP)))
    const rows = Math.ceil(list.length / perRow)
    if (figuresEl) figuresEl.style.height = `${rows * ROW_H + (rows - 1) * ROW_GAP}px`
    list.forEach((fig, i) => {
      const row = Math.floor(i / perRow), col = i % perRow
      const offset = INSET + col * (FIG + GAP)
      this.applyFigurePosition(fig, 'right', 1, offset, row * (ROW_H + ROW_GAP) + ROW_H / 2, `${SPINE}px`)
      this.setConnectorVisible(fig, col === 0)
    })
  }

  // Re-run layoutMonth for every month that currently has figures. Used
  // by the ResizeObserver when the container width changes (window
  // resize, orientation change, or the timeline transitioning from
  // hidden -> visible via the build-photos toggle).
  relayoutAllMonths() {
    if (!this.monthFigures) return
    for (const monthKey of Object.keys(this.monthFigures)) {
      const list = this.monthFigures[monthKey]
      if (list && list.length > 0) this.layoutMonth(monthKey)
    }
    this.updateCenterFades()
  }

  applyFigurePosition(fig, side, depth, offset, rowCenter, spine = '50%') {
    // When `rowCenter` is supplied the figure is positioned at that
    // explicit y offset (in pixels from the figures container top); the
    // figure's `-translate-y-1/2` Tailwind class then centers it on that
    // line. Omitting `rowCenter` falls back to the Tailwind `top-1/2`
    // class which targets the middle of a single-row 140px container.
    const topStyle = (rowCenter != null) ? `top: ${rowCenter}px; ` : ''
    fig.style.cssText = `${topStyle}${side === 'left' ? `right: calc(${spine === '50%' ? '50%' : `100% - ${spine}`} + ${offset}px);` : `left: calc(${spine} + ${offset}px);`} width: 140px;`
    // Remove any previously added sibling connector
    if (fig._connector && fig._connector.parentNode) {
      fig._connector.parentNode.removeChild(fig._connector)
      fig._connector = null
    }
    const connector = fig.querySelector('.connector-line') || (() => {
      const c = document.createElement('div')
      c.className = 'connector-line absolute top-1/2 h-0.5 bg-yellow-400 z-0 flex items-center pointer-events-none'
      const circle = document.createElement('div')
      circle.className = 'timeline-connector-circle w-3 h-3 bg-yellow-400 rounded-full absolute top-1/2 z-30'
      c.appendChild(circle)
      fig.appendChild(c)
      return c
    })()
    // Match original ERB logic: anchor from figure edge toward the timeline
    connector.className = `connector-line absolute top-1/2 ${side === 'left' ? 'right-full' : 'left-full'} h-0.5 bg-yellow-400 z-0 flex items-center pointer-events-none`
    connector.style.width = `${offset}px`
    connector.style.transform = 'translateY(-50%)'
    connector.style.left = 'auto'
    connector.style.right = 'auto'
    if (side === 'left') {
      connector.style.right = `-${offset}px`
    } else {
      connector.style.left = `-${offset}px`
    }
    const circle = connector.querySelector('.timeline-connector-circle')
    if (circle) {
      // Circle at figure end
      if (side === 'left') {
        circle.style.left = '0'
        circle.style.right = 'auto'
      } else {
        circle.style.right = '0'
        circle.style.left = 'auto'
      }
      circle.style.transform = 'translateY(-50%)'
    }
  }

  queueConnectorSpark(fig) {
    // Delay to allow layout to settle
    requestAnimationFrame(() => {
      const connector = fig.querySelector('.connector-line')
      if (!connector) return
      const width = parseFloat(connector.style.width || '0')
      if (!width || width < 10) return
      const isLeft = connector.classList.contains('right-full') // means figure is left side
      // Create spark element
      const spark = document.createElement('div')
      spark.className = `timeline-spark ${isLeft ? 'spark-left' : 'spark-right'}`
      // Position at start of connector (near spine)
      spark.style.position = 'absolute'
      spark.style.top = '50%'
      spark.style.width = '8px'
      spark.style.height = '8px'
      spark.style.borderRadius = '9999px'
      spark.style.background = 'radial-gradient(circle, #fde047 0%, #f59e0b 60%, rgba(0,0,0,0) 70%)'
      spark.style.boxShadow = '0 0 10px rgba(250,204,21,0.9)'
      spark.style.transform = 'translateY(-50%)'
      spark.style.pointerEvents = 'none'
      // Anchor spark to correct edge of connector
      if (isLeft) {
        // left-side figure: connector origin (figure) at left:0, spine at +width
        spark.style.left = '0'
      } else {
        // right-side figure: connector origin (figure) at right:0, spine at -width
        spark.style.right = '0'
      }
      // Set animation custom property for distance
      spark.style.setProperty('--spark-distance', `${width}px`)
      connector.appendChild(spark)
      // Kick off animation, then cleanup
      spark.classList.add('run')
      setTimeout(() => {
        spark.remove()
      }, 700)
    })
  }

  updateSentinelPosition() {
    if (!this.hasSentinelTarget || !this.itemsEl) return
    let lastIdx = -1
    let lastWrapper = null
    for (const [monthKey, figs] of Object.entries(this.monthFigures)) {
      if (!figs || figs.length === 0) continue
      const idx = this.monthIndexByKey[monthKey]
      if (typeof idx === 'number' && idx > lastIdx) {
        const wrapper = this.itemsEl.querySelector(`.timeline-date-group[data-date="${monthKey}"]`)
        if (wrapper) {
          lastIdx = idx
          lastWrapper = wrapper
        }
      }
    }
    if (lastWrapper) {
      // Place sentinel after the last non-empty month group within items container
      if (this.sentinelTarget.parentElement !== this.itemsEl) {
        this.itemsEl.appendChild(this.sentinelTarget)
      }
      if (lastWrapper.nextSibling) {
        this.itemsEl.insertBefore(this.sentinelTarget, lastWrapper.nextSibling)
      } else {
        this.itemsEl.appendChild(this.sentinelTarget)
      }
    }
  }

  setConnectorVisible(fig, visible) {
    const connector = fig.querySelector('.connector-line')
    if (!connector) return
    connector.style.display = visible ? '' : 'none'
  }

  showLoader(show) {
    if (!this.hasLoaderTarget) return
    if (show) {
      this.loaderTarget.classList.remove('hidden')
      // Ensure loader is at the end, right before sentinel
      if (this.loaderTarget.parentElement !== this.itemsEl) {
        this.itemsEl.appendChild(this.loaderTarget)
      }
      if (this.hasSentinelTarget) {
        this.itemsEl.insertBefore(this.loaderTarget, this.sentinelTarget)
      }
    } else {
      this.loaderTarget.classList.add('hidden')
    }
  }
}


